import { GlobalBenefitsBadge } from '@/components/GlobalBenefitsBadge';
import { JointPolicyDeleteDialog } from '@/components/JointPolicyDeleteDialog';
import { ProductDetails } from '@/components/ProductDetails';
import { UnderwritingStatus } from '@client/features/underwriting-eligibility';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog';
import {
  CreateQuoteButton,
  MissingAdvisorAddressDialog,
  QuotePriceDisplay,
  QuoteProductInfo,
  QuoteROIDisplay,
  useIllustrationManagement,
  useIllustrationPolling,
  usePolicyManagement,
  useQuoteCardDialogs,
  useShopInsuranceContext,
  type QuoteWithROI,
  type ShopInsuranceQuoteCardProps,
} from '@/features/shop-insurance';
import { Button, Card, Tooltip, TooltipContent, TooltipTrigger, cn } from '@design-system';

import { ChevronUp } from 'lucide-react';

import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useSelector } from 'react-redux';

import { selectClient } from '@/model';
import { getPolicyTypeFromProductName } from '@/util/findPartnerPolicyFundingSourceId';
import { CARRIERS_WITHOUT_QUOTE_CREATION } from '@shared/constants';

export function LifeInsuranceQuoteCard({
  quote,
  isExpanded,
  onToggleExpand,
  onClose,
  hasHighestROI = false,
  isSelected = false,
  fundingSourceId = null,
  selectedTab = 'single',
  clientPartner = null,
  selectedPremium,
  testId,
}: ShopInsuranceQuoteCardProps) {
  const { flags } = useFeatureFlags();
  const client = useSelector(selectClient);
  const { isGapFillMode } = useShopInsuranceContext();
  useIllustrationPolling();

  const {
    handleAddPolicy,
    handleRemovePolicy,
    deletePolicyForClientOnly,
    deletePolicyForBothClients,
  } = usePolicyManagement({
    quote,
    selectedTab,
    clientPartner,
    fundingSourceId,
    onClose,
  });

  const {
    hasIllustrationPdf,
    hasIllustrationError,
    isCreatingIllustration,
    handleViewPdf,
    handleCreateIllustration,
    isMissingAdvisorAddressDialogOpen,
    closeMissingAdvisorAddressDialog,
    handleMissingAdvisorAddressResolved,
  } = useIllustrationManagement({
    quote,
    selectedPremium: isGapFillMode ? quote.premiumAmount : selectedPremium,
    clientPartner,
  });

  const {
    showJointPolicyDeleteDialog,
    jointPolicyDeleteMode,
    showQuoteGenerationInfoDialog,
    setJointPolicyDeleteMode,
    handleJointPolicyDeleteCancel,
    handleJointPolicyDeleteConfirm,
    showJointDialog,
    showQuoteGenerationInfoDialogFn,
    hideQuoteGenerationInfoDialog,
  } = useQuoteCardDialogs();

  const quoteWithROI = quote as QuoteWithROI;
  const displayROI = quoteWithROI.formattedROI;
  const isQuoteCreationUnavailable = CARRIERS_WITHOUT_QUOTE_CREATION.includes(
    quote.carrierName as any,
  );
  const isUnavailableInGapFill = isGapFillMode && hasIllustrationError;

  const totalCost = client?.allPhaseCosts?.allPhaseInflatedProfessionalShareCost ?? 0;
  const coveragePercentage =
    isGapFillMode && totalCost > 0 && quoteWithROI.projectedCostCoverage
      ? (quoteWithROI.projectedCostCoverage / totalCost) * 100
      : undefined;

  return (
    <>
      <Card
        data-testid={testId}
        className={cn(
          'hover:border-primary hover:ring-primary/10 w-full cursor-pointer gap-0 p-0 ring-4 ring-transparent transition-all duration-200',
          {
            'border-primary/50 bg-linear-to-r from-primary/5 to-primary/1 shadow-none': isSelected,
            'pointer-events-none border-gray-200 opacity-50 grayscale hover:border-gray-200 hover:ring-transparent':
              isUnavailableInGapFill,
          },
        )}
        onClick={isUnavailableInGapFill ? undefined : isSelected ? undefined : onToggleExpand}
      >
        <div className="relative">
          <div className="grid grid-cols-[1fr_auto] gap-4 p-4">
            <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
              <div className="flex flex-col gap-2">
                <QuoteProductInfo
                  carrierName={quote.carrierName}
                  productName={quote.productName}
                  showLogo={true}
                  showRating={true}
                />
                <div className="flex max-w-xs flex-wrap gap-2">
                  <UnderwritingStatus
                    productName={quote.productName}
                    fundingPolicyType={quote.fundingPolicyType}
                    policyType={quote.policyType}
                  />
                  <GlobalBenefitsBadge hasGlobalBenefits={quote.hasGlobalBenefits} />
                </div>
              </div>
              <QuoteROIDisplay
                projectedCostCoverage={quoteWithROI.projectedCostCoverage}
                partnerProjectedCostCoverage={quoteWithROI.partnerProjectedCostCoverage}
                policyType={getPolicyTypeFromProductName(
                  quote.productName,
                  quote.policyType === 'Joint Life',
                )}
                displayROI={displayROI}
              />
              <QuotePriceDisplay
                price={quote.premiumAmount}
                fundingOption={quote.productFundingOption}
                coveragePercentage={coveragePercentage}
              />
            </div>
            <div className="flex shrink-0 flex-col gap-2 self-start">
              {isGapFillMode ? (
                <>
                  {hasIllustrationPdf && (
                    <>
                      {isSelected ? (
                        <Button
                          type="button"
                          variant="destructive"
                          className="bg-destructive/10 text-destructive hover:bg-destructive/15"
                          onClick={clickEvent => handleRemovePolicy(clickEvent, showJointDialog)}
                        >
                          Remove Product
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={handleAddPolicy}
                        >
                          Select Product
                        </Button>
                      )}
                    </>
                  )}
                  {isUnavailableInGapFill ? (
                    <Button
                      variant="outline"
                      disabled
                      className="pointer-events-auto w-full opacity-100"
                    >
                      Not Available
                    </Button>
                  ) : (
                    <CreateQuoteButton
                      testId="shop-insurance-quote-check-button"
                      onClick={
                        hasIllustrationPdf
                          ? handleViewPdf
                          : event =>
                              handleCreateIllustration(event, showQuoteGenerationInfoDialogFn)
                      }
                      disabled={isCreatingIllustration}
                      isLoading={isCreatingIllustration}
                      isCompleted={Boolean(hasIllustrationPdf)}
                      defaultText="Check Availability"
                      loadingText="Checking..."
                      completedText="View Quote"
                      className="w-full"
                    />
                  )}
                </>
              ) : (
                <>
                  {isSelected ? (
                    <Button
                      type="button"
                      variant="destructive"
                      className="bg-destructive/10 text-destructive hover:bg-destructive/15"
                      onClick={clickEvent => handleRemovePolicy(clickEvent, showJointDialog)}
                    >
                      Remove Product
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleAddPolicy}
                    >
                      Select Product
                    </Button>
                  )}
                  {isQuoteCreationUnavailable ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <CreateQuoteButton
                          testId="shop-insurance-quote-download-button"
                          onClick={() => {}}
                          isLoading={false}
                          disabled
                          isCompleted={false}
                          defaultText={
                            <>
                              Illustration Download
                              <br />
                              Coming Soon
                            </>
                          }
                          className="h-auto w-full"
                        />
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="center"
                        className="max-w-80 whitespace-normal"
                      >
                        We are currently implementing illustration download for this policy and will
                        have it soon, but you can model it in the meantime, and can reach out to our
                        customer support to help you get the illustration manually.
                      </TooltipContent>
                    </Tooltip>
                  ) : flags.nycLifeDemo ? (
                    <Tooltip>
                      <TooltipTrigger>
                        <CreateQuoteButton
                          testId="shop-insurance-quote-download-button"
                          onClick={() => {}}
                          isLoading={false}
                          disabled
                          isCompleted={false}
                          defaultText="Create Quote"
                          className="h-auto w-full"
                        />
                      </TooltipTrigger>
                      <TooltipContent>This feature is not available for the demo</TooltipContent>
                    </Tooltip>
                  ) : (
                    <CreateQuoteButton
                      testId="shop-insurance-quote-download-button"
                      onClick={
                        hasIllustrationPdf
                          ? handleViewPdf
                          : event =>
                              handleCreateIllustration(event, showQuoteGenerationInfoDialogFn)
                      }
                      disabled={isCreatingIllustration}
                      isLoading={isCreatingIllustration}
                      isCompleted={Boolean(hasIllustrationPdf)}
                      defaultText="Create Quote"
                      loadingText="Generating"
                      completedText="View Quote"
                      className="w-full"
                    />
                  )}
                </>
              )}
            </div>
          </div>
          <div className="border-border mt-0 border-t">
            {isUnavailableInGapFill ? (
              <div className="p-4">
                <span className="text-muted-foreground text-sm">
                  This product isn't available at this premium.
                </span>
              </div>
            ) : (
              <div
                data-testid="shop-insurance-quote-expand-button"
                className={cn(
                  'flex items-center justify-between p-4',
                  !isSelected && 'cursor-pointer',
                )}
                onClick={clickEvent => {
                  clickEvent.stopPropagation();
                  onToggleExpand();
                }}
              >
                <span className="sm:text-md text-primary-dark select-none text-sm font-medium">
                  Product Details
                </span>
                <ChevronUp
                  className={cn('text-primary-dark size-4 transition-transform duration-200', {
                    'rotate-180': !isExpanded,
                  })}
                />
              </div>
            )}
          </div>
          {!isUnavailableInGapFill && isExpanded && (
            <div className="px-4 pb-4 [&>div]:mt-0">
              <ProductDetails quote={quote} />
            </div>
          )}
        </div>
      </Card>

      <JointPolicyDeleteDialog
        open={showJointPolicyDeleteDialog}
        onClose={handleJointPolicyDeleteCancel}
        onConfirm={mode =>
          handleJointPolicyDeleteConfirm(
            mode,
            deletePolicyForBothClients,
            deletePolicyForClientOnly,
          )
        }
        deleteMode={jointPolicyDeleteMode}
        onDeleteModeChange={setJointPolicyDeleteMode}
        clientName={client?.clientFirstName}
        partnerName={clientPartner?.clientFirstName}
      />

      <UnsavedChangesDialog
        showWarning={showQuoteGenerationInfoDialog}
        handleCancelClose={hideQuoteGenerationInfoDialog}
        handleConfirmClose={hideQuoteGenerationInfoDialog}
        dialogTitle="Please wait for current quote generation"
        dialogDescription="We're currently generating a quote for another product. Since this process takes 1-2 minutes, please wait for it to complete before creating a new quote. You can still view any previously generated quotes."
        primaryButtonText="Got it"
        secondaryButtonText=""
        destructive={false}
        showIcon={true}
      />

      <MissingAdvisorAddressDialog
        open={isMissingAdvisorAddressDialogOpen}
        onClose={closeMissingAdvisorAddressDialog}
        onResolved={handleMissingAdvisorAddressResolved}
      />
    </>
  );
}
